import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import VenueImageUpload from "@/components/VenueImageUpload";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { 
  Building2, 
  Calendar, 
  TrendingUp, 
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  Users,
  BarChart3,
  Settings,
  Megaphone,
  Plus,
  Edit2,
  Trash2,
  MapPin,
  Star
} from "lucide-react";

const VenueOwnerDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [venues, setVenues] = useState([]);
  const [newVenue, setNewVenue] = useState({
    name: "",
    location: "",
    capacity: 14,
    pricePerHour: 500,
    format: "7v7",
    images: [],
    facilities: "",
    openingHours: "08:00-23:00"
  });

  useEffect(() => {
    if (user?.role !== "venue_owner") {
      navigate("/");
      return;
    }
    fetchDashboard();
    fetchBookings();
    fetchVenues();
  }, [user]);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/venue-owner/dashboard`);
      setDashboard(response.data);
    } catch (error) {
      console.error("Error fetching dashboard:", error);
      toast.error("Dashboard yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/venue-owner/bookings`);
      setBookings(response.data);
    } catch (error) {
      console.error("Error fetching bookings:", error);
    }
  };

  const fetchVenues = async () => {
    try {
      const response = await axios.get(`${API}/venue-owner/venues`);
      setVenues(response.data);
    } catch (error) {
      console.error("Error fetching venues:", error);
      toast.error("Sahalar yüklenemedi");
    }
  };

  const handleCreateVenue = async () => {
    if (!newVenue.name || !newVenue.location) {
      toast.error("Lütfen saha adı ve konum girin");
      return;
    }

    try {
      const venueData = {
        ...newVenue,
        images: newVenue.images || [],
        amenities: newVenue.facilities ? newVenue.facilities.split(',').map(f => f.trim()) : []
      };

      await axios.post(`${API}/venues`, venueData);
      toast.success("Saha başarıyla oluşturuldu!");
      
      // Reset form
      setNewVenue({
        name: "",
        location: "",
        capacity: 14,
        pricePerHour: 500,
        format: "7v7",
        images: [],
        facilities: "",
        openingHours: "08:00-23:00"
      });
      
      fetchVenues();
      fetchDashboard();
    } catch (error) {
      console.error("Error creating venue:", error);
      toast.error(error.response?.data?.detail || "Saha oluşturulamadı");
    }
  };

  const handleDeleteVenue = async (venueId) => {
    if (!window.confirm("Bu sahayı silmek istediğinizden emin misiniz?")) return;

    try {
      await axios.delete(`${API}/venues/${venueId}`);
      toast.success("Saha silindi");
      fetchVenues();
      fetchDashboard();
    } catch (error) {
      console.error("Error deleting venue:", error);
      toast.error("Saha silinemedi");
    }
  };

  const handleBookingStatus = async (bookingId, status) => {
    setProcessing(bookingId);
    try {
      await axios.put(`${API}/venue-owner/bookings/${bookingId}/status`, { status });
      toast.success(`Rezervasyon ${status === 'confirmed' ? 'onaylandı' : 'iptal edildi'}`);
      fetchBookings();
      fetchDashboard();
    } catch (error) {
      toast.error("İşlem başarısız");
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-[80vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Yükleniyor...</p>
          </div>
        </div>
      </div>
    );
  }

  const stats = dashboard?.stats || {};
  const pendingBookings = bookings.filter(b => b.status === 'pending');

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <main className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-2">
                Halı Saha Yönetici Paneli
              </h1>
              <p className="text-gray-600">Sahalarınızı yönetin ve rezervasyonları takip edin</p>
            </div>
            <Button 
              variant="outline"
              onClick={() => navigate("/claim-venue")}
              className="border-blue-300 text-blue-600 hover:bg-blue-50"
            >
              <Building2 className="h-4 w-4 mr-2" />
              Sahalarımı Talep Et
            </Button>
          </div>
        </div>

        <Tabs defaultValue="dashboard" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="dashboard">📊 Dashboard</TabsTrigger>
            <TabsTrigger value="venues">🏟️ Sahalarım ({venues.length})</TabsTrigger>
            <TabsTrigger value="add-venue">➕ Yeni Saha</TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="border-l-4 border-l-blue-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Sahalarım</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <span className="text-3xl font-bold text-blue-600">
                  {stats.totalVenues || 0}
                </span>
                <Building2 className="h-8 w-8 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Bugünkü Rezervasyonlar</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <span className="text-3xl font-bold text-green-600">
                  {stats.todayBookings || 0}
                </span>
                <Calendar className="h-8 w-8 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Doluluk Oranı</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <span className="text-3xl font-bold text-purple-600">
                  %{stats.occupancyRate || 0}
                </span>
                <BarChart3 className="h-8 w-8 text-purple-500 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-emerald-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Toplam Gelir</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <span className="text-3xl font-bold text-emerald-600">
                  ₺{stats.totalRevenue || 0}
                </span>
                <DollarSign className="h-8 w-8 text-emerald-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-4">Hızlı Erişim</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Button
              className="h-20 text-lg bg-gradient-to-r from-blue-500 to-blue-600"
              onClick={() => navigate("/reservation-calendar")}
            >
              <Calendar className="h-6 w-6 mr-2" />
              Rezervasyon Takvimi
            </Button>
            <Button
              className="h-20 text-lg bg-gradient-to-r from-emerald-500 to-emerald-600"
              onClick={() => navigate("/dynamic-pricing")}
            >
              <DollarSign className="h-6 w-6 mr-2" />
              Dinamik Fiyatlandırma
            </Button>
            <Button
              className="h-20 text-lg bg-gradient-to-r from-purple-500 to-purple-600"
              onClick={() => navigate("/revenue-analytics")}
            >
              <TrendingUp className="h-6 w-6 mr-2" />
              Gelir Analizi
            </Button>
            {dashboard?.venues?.length > 0 && (
              <Button
                className="h-20 text-lg bg-gradient-to-r from-indigo-500 to-indigo-600"
                onClick={() => navigate(`/venue-settings/${dashboard.venues[0].id}`)}
              >
                <Settings className="h-6 w-6 mr-2" />
                Saha Ayarları
              </Button>
            )}
            <Button
              className="h-20 text-lg bg-gradient-to-r from-pink-500 to-pink-600"
              onClick={() => navigate("/announcements-manager")}
            >
              <Megaphone className="h-6 w-6 mr-2" />
              Duyuru Yönetimi
            </Button>
            <Button
              variant="outline"
              className="h-20 text-lg border-2"
              onClick={() => navigate("/venue-admin")}
            >
              <Users className="h-6 w-6 mr-2" />
              Tüm Rezervasyonlar
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pending Bookings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-orange-600" />
                Bekleyen Rezervasyonlar
              </CardTitle>
              <CardDescription>Onay bekleyen rezervasyonlar</CardDescription>
            </CardHeader>
            <CardContent>
              {pendingBookings.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Clock className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>Bekleyen rezervasyon yok</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingBookings.slice(0, 5).map((booking) => (
                    <div 
                      key={booking.id}
                      className="p-4 border rounded-lg hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-semibold">{booking.venueName}</h3>
                          <p className="text-sm text-gray-600">{booking.userName}</p>
                        </div>
                        <Badge variant="outline" className="bg-orange-50 text-orange-700">
                          Bekliyor
                        </Badge>
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {booking.date}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {booking.time}
                        </span>
                        <span className="flex items-center gap-1">
                          <DollarSign className="h-4 w-4" />
                          ₺{booking.price}
                        </span>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => handleBookingStatus(booking.id, 'confirmed')}
                          disabled={processing === booking.id}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Onayla
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          className="flex-1"
                          onClick={() => handleBookingStatus(booking.id, 'cancelled')}
                          disabled={processing === booking.id}
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Reddet
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Today's Schedule */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-blue-600" />
                Bugünkü Program
              </CardTitle>
              <CardDescription>Bugün için onaylı rezervasyonlar</CardDescription>
            </CardHeader>
            <CardContent>
              {dashboard?.todayBookings?.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>Bugün rezervasyon yok</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {dashboard?.todayBookings?.map((booking) => (
                    <div 
                      key={booking.id}
                      className="p-3 border rounded-lg"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-sm">{booking.venueName}</h3>
                          <p className="text-sm text-gray-600">{booking.userName}</p>
                        </div>
                        <Badge className="bg-green-100 text-green-800">
                          {booking.time}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Upcoming Matches */}
        {dashboard?.upcomingMatches?.length > 0 && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-emerald-600" />
                Yaklaşan Maçlar
              </CardTitle>
              <CardDescription>Sahalarınızda düzenlenecek maçlar</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {dashboard.upcomingMatches.map((match) => (
                  <div 
                    key={match.id}
                    className="p-4 border rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => navigate(`/matches/${match.id}`)}
                  >
                    <h3 className="font-semibold mb-2">{match.venueName}</h3>
                    <div className="space-y-1 text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {match.date}
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {match.time}
                      </div>
                      <div className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        {match.players?.length || 0} oyuncu
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
          </TabsContent>

          {/* Venues List Tab */}
          <TabsContent value="venues" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Sahalarım</CardTitle>
                <CardDescription>Tüm sahalarınızı görüntüleyin ve yönetin</CardDescription>
              </CardHeader>
              <CardContent>
                {venues.length === 0 ? (
                  <div className="text-center py-12">
                    <Building2 className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-600 mb-2">Henüz Saha Eklemediniz</h3>
                    <p className="text-gray-500 mb-4">"Yeni Saha" sekmesinden ilk sahanızı ekleyin</p>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {venues.map((venue) => (
                      <Card key={venue.id} className="hover:shadow-lg transition-shadow">
                        <CardHeader>
                          <div className="flex items-start justify-between">
                            <div>
                              <CardTitle className="text-xl">{venue.name}</CardTitle>
                              <CardDescription className="flex items-center gap-1 mt-1">
                                <MapPin className="h-4 w-4" />
                                {venue.location}
                              </CardDescription>
                            </div>
                            <Badge variant="outline">{venue.format || "7x7"}</Badge>
                          </div>
                        </CardHeader>
                        <CardContent>
                          {venue.images && venue.images.length > 0 && (
                            <img
                              src={venue.images[0]}
                              alt={venue.name}
                              className="w-full h-40 object-cover rounded-lg mb-4"
                              onError={(e) => e.target.style.display = 'none'}
                            />
                          )}
                          <div className="space-y-2">
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-600">Kapasite:</span>
                              <span className="font-semibold">{venue.capacity} kişi</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-600">Saat Başı Fiyat:</span>
                              <span className="font-semibold text-emerald-600">{venue.pricePerHour} TL</span>
                            </div>
                            {venue.openingHours && (
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-600">Açık Saatler:</span>
                                <span className="font-semibold">{venue.openingHours}</span>
                              </div>
                            )}
                          </div>
                          <div className="mt-4 flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1"
                              onClick={() => navigate(`/venue-settings/${venue.id}`)}
                            >
                              <Edit2 className="h-4 w-4 mr-2" />
                              Düzenle
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1"
                              onClick={() => navigate(`/reservation-calendar?venueId=${venue.id}`)}
                            >
                              <Calendar className="h-4 w-4 mr-2" />
                              Takvim
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDeleteVenue(venue.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Add Venue Tab */}
          <TabsContent value="add-venue" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Yeni Saha Ekle</CardTitle>
                <CardDescription>Yeni bir halı saha ekleyin ve rezervasyon almaya başlayın</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Saha Adı *</Label>
                      <Input
                        value={newVenue.name}
                        onChange={(e) => setNewVenue({...newVenue, name: e.target.value})}
                        placeholder="Örn: Krampon Arena"
                      />
                    </div>
                    <div>
                      <Label>Konum *</Label>
                      <Input
                        value={newVenue.location}
                        onChange={(e) => setNewVenue({...newVenue, location: e.target.value})}
                        placeholder="Örn: İstanbul, Kadıköy"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <Label>Kapasite (Kişi)</Label>
                      <Input
                        type="number"
                        value={newVenue.capacity}
                        onChange={(e) => setNewVenue({...newVenue, capacity: parseInt(e.target.value)})}
                      />
                    </div>
                    <div>
                      <Label>Saat Başı Fiyat (TL)</Label>
                      <Input
                        type="number"
                        value={newVenue.pricePerHour}
                        onChange={(e) => setNewVenue({...newVenue, pricePerHour: parseFloat(e.target.value)})}
                      />
                    </div>
                    <div>
                      <Label>Format</Label>
                      <select
                        className="w-full border rounded-md p-2"
                        value={newVenue.format}
                        onChange={(e) => setNewVenue({...newVenue, format: e.target.value})}
                      >
                        <option value="5v5">5v5</option>
                        <option value="6v6">6v6</option>
                        <option value="7v7">7v7</option>
                        <option value="8v8">8v8</option>
                        <option value="11v11">11v11</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <Label>Açılış Saatleri</Label>
                    <Input
                      value={newVenue.openingHours}
                      onChange={(e) => setNewVenue({...newVenue, openingHours: e.target.value})}
                      placeholder="Örn: 08:00-23:00"
                    />
                  </div>

                  <div>
                    <Label>Saha Fotoğrafları</Label>
                    <VenueImageUpload 
                      images={newVenue.images}
                      onImagesUpdate={(images) => setNewVenue({...newVenue, images})}
                    />
                  </div>

                  <div>
                    <Label>Olanaklar (virgülle ayırın)</Label>
                    <Textarea
                      value={newVenue.facilities}
                      onChange={(e) => setNewVenue({...newVenue, facilities: e.target.value})}
                      placeholder="Duş, Soyunma Odası, Otopark, Kantin"
                      rows={2}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Örn: Duş, Soyunma Odası, Otopark, Kantin, Kafe, Tribün
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <Button 
                      onClick={handleCreateVenue}
                      className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-600"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Saha Oluştur
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => setNewVenue({
                        name: "",
                        location: "",
                        capacity: 14,
                        pricePerHour: 500,
                        format: "7v7",
                        images: "",
                        facilities: "",
                        openingHours: "08:00-23:00"
                      })}
                    >
                      Temizle
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default VenueOwnerDashboard;
